import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { TaskService } from '../../../services/task.service';
import { buildCreatePayload, createTaskForm, getFormDeadline, syncDeadlineTime } from '../tasks.form';
import { ResolvedTaskLink, Task, TaskReminderPayload, TaskUser } from '../tasks.types';
import { toPickerValues } from '../tasks.utils';
import { TaskReminderRow } from '../task-reminders/task-reminders.component';

let nextReminderId = 0;

const toReminderPayload = (row: TaskReminderRow): TaskReminderPayload =>
  row.before_deadline_minutes !== null
    ? { before_deadline_minutes: row.before_deadline_minutes }
    : { remind_at: row.remind_at ?? undefined };

/**
 * Creates a task: what it is about, who does it and who follows it, when it is due, whom it concerns and when to
 * remind. It sits in a stacked sidebar and closes with the task the backend created.
 */
@Component({
  selector: 'app-task-form',
  templateUrl: './task-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  host: { class: 'block h-full min-h-0' },
})
export class TaskFormComponent implements OnInit {
  /** What the task is linked to from the start, e.g. the card it is created from */
  @Input() links: ResolvedTaskLink[] = [];
  /** The deadline the task starts with (ISO), e.g. the end of today for a task of the day */
  @Input() deadline: string | null = null;
  /** The new task, or nothing when the form is closed */
  @Output() close = new EventEmitter<Task | void>();

  private readonly taskService = inject(TaskService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly form = createTaskForm();
  /** The reminders are sent with the task: they can not be saved before it exists */
  protected reminders: TaskReminderRow[] = [];
  protected users: TaskUser[] = [];
  protected canLink = false;
  protected loading = true;
  protected loadFailed = false;
  protected saving = false;

  protected get hasDeadline(): boolean {
    return !!getFormDeadline(this.form);
  }

  /** The prefilled links do not count: closing a form with only them loses nothing */
  protected get hasChanges(): boolean {
    return this.form.dirty || this.reminders.length > 0;
  }

  ngOnInit(): void {
    // Cards, contacts and companies belong to CRM 2.0; a link that is given is shown anyway
    this.canLink = this.taskService.isCrmV2 || this.links.length > 0;
    this.form.controls.links.setValue(this.links);
    this.form.controls.assignee_id.setValue(this.taskService.currentUserId);
    if (this.deadline) {
      const { date, time } = toPickerValues(this.deadline);
      this.form.controls.deadline_date.setValue(date);
      syncDeadlineTime(this.form);
      if (date) this.form.controls.deadline_time.setValue(time);
    }
    // What the form starts with is not a change: closing it asks only about what was typed
    this.form.markAsPristine();

    // A reminder before the deadline can not outlive it
    this.form.controls.deadline_date.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((date) => {
      if (!date) this.reminders = this.reminders.filter((row) => row.before_deadline_minutes === null);
    });
    this.load();
  }

  protected load(): void {
    this.loading = true;
    this.loadFailed = false;
    this.taskService
      .getUsers()
      .pipe(
        finalize(() => (this.loading = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (users) => (this.users = users),
        error: (error) => {
          this.loadFailed = true;
          this.taskService.error(error);
        },
      });
  }

  protected addReminder(payload: TaskReminderPayload): void {
    const beforeMinutes = payload.before_deadline_minutes ?? null;
    const remindAt = payload.remind_at ?? null;
    // The same time twice is the same reminder
    const exists = this.reminders.some((row) =>
      beforeMinutes !== null
        ? row.before_deadline_minutes === beforeMinutes
        : row.before_deadline_minutes === null &&
          !!row.remind_at &&
          !!remindAt &&
          new Date(row.remind_at).getTime() === new Date(remindAt).getTime(),
    );
    if (exists) return;

    this.reminders = [
      ...this.reminders,
      { id: `new-${nextReminderId++}`, before_deadline_minutes: beforeMinutes, remind_at: remindAt, sent_at: null },
    ];
    this.form.markAsDirty();
  }

  protected removeReminder(row: TaskReminderRow): void {
    this.reminders = this.reminders.filter((item) => item.id !== row.id);
  }

  protected submit(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid || this.saving) return;

    this.saving = true;
    this.taskService
      .createTask(buildCreatePayload(this.form, this.reminders.map(toReminderPayload)))
      .pipe(
        finalize(() => (this.saving = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (task) => {
          this.taskService.success('crm_tasks.form.created');
          this.close.emit(task);
        },
        error: (error) => this.taskService.error(error),
      });
  }

  /** Called by the sidebar close button and the backdrop: what was typed asks first */
  requestClose(): void {
    if (this.saving) return;

    this.taskService
      .confirm(this.hasChanges ? 'crm_tasks.form.unsaved_confirm' : null)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.close.emit());
  }
}
